<script setup>
import { Button, Card, Input, InputNumber, Select, Switch } from 'ant-design-vue';
import {
    BOOKING_PERIODS,
    BOOKING_WEEKDAYS,
    useBookingServiceSettings
} from '@/composables/useBookingServiceSettings';

const {
    canUpdate,
    loading,
    saving,
    savingGrant,
    extraStart,
    selectedCode,
    form,
    grants,
    grantDraft,
    serviceOptions,
    assignmentOptions,
    editDatePolicyOptions,
    slotOptions,
    onSelectService,
    toggleSlot,
    addExtraSlot,
    toggleBlackout,
    save,
    saveGrant,
    permissionLabel
} = useBookingServiceSettings();
</script>

<template>
    <div class="params-page pb-10">
        <Card :bordered="false" class="admin-panel" :loading="loading">
            <div class="params-head">
                <label>
                    <span>服務</span>
                    <Select
                        :value="selectedCode"
                        style="width: 220px"
                        :disabled="loading"
                        :options="serviceOptions"
                        @change="onSelectService"
                    />
                </label>
                <Button v-if="canUpdate" type="primary" :loading="saving" @click="save">儲存參數</Button>
            </div>
        </Card>

        <div class="params-grid">
            <Card :bordered="false" class="admin-panel" :loading="loading">
                <h2>派單規則</h2>
                <div class="params-fields">
                    <label>
                        <span>學員怎麼約到人</span>
                        <Select
                            v-model:value="form.assignment_mode"
                            :disabled="!canUpdate"
                            :options="assignmentOptions"
                        />
                    </label>
                    <label>
                        <span>整月都沒圈日期時</span>
                        <Select
                            v-model:value="form.schedule_date_policy"
                            :disabled="!canUpdate"
                            :options="editDatePolicyOptions"
                        />
                    </label>
                </div>
            </Card>

            <Card :bordered="false" class="admin-panel" :loading="loading">
                <div class="params-head">
                    <h2>行政授權</h2>
                    <Button v-if="canUpdate" :loading="savingGrant" @click="saveGrant">儲存授權</Button>
                </div>
                <ul v-if="grants.length" class="params-grants">
                    <li v-for="row in grants" :key="row.core_member_id">
                        {{ row.core_member_id }}：{{ (row.permissions || []).map(permissionLabel).join('、') || '無' }}
                    </li>
                </ul>
                <div v-if="canUpdate" class="params-create">
                    <Input v-model:value="grantDraft.core_member_id" placeholder="行政的會員編號" />
                    <label class="params-chip">
                        <input v-model="grantDraft.permissions" type="checkbox" value="view" />
                        查看
                    </label>
                    <label class="params-chip">
                        <input v-model="grantDraft.permissions" type="checkbox" value="set_dates" />
                        圈日期
                    </label>
                    <label class="params-chip">
                        <input v-model="grantDraft.permissions" type="checkbox" value="proxy_schedule" />
                        代排
                    </label>
                </div>
            </Card>
        </div>

        <div class="params-grid">
            <Card :bordered="false" class="admin-panel" :loading="loading">
                <h2>可約區間</h2>
                <div class="params-fields">
                    <label>
                        <span>最早（今天後幾天）</span>
                        <InputNumber
                            v-model:value="form.booking_window_min_days"
                            :min="0"
                            :max="365"
                            :disabled="!canUpdate"
                        />
                    </label>
                    <label>
                        <span>最遠（今天後幾天）</span>
                        <InputNumber
                            v-model:value="form.booking_window_max_days"
                            :min="0"
                            :max="365"
                            :disabled="!canUpdate"
                        />
                    </label>
                </div>
            </Card>

            <Card :bordered="false" class="admin-panel" :loading="loading">
                <h2>同時段上限</h2>
                <div class="params-fields">
                    <label>
                        <span>全站上限</span>
                        <InputNumber
                            v-model:value="form.offline_capacity_per_slot"
                            :min="1"
                            :max="50"
                            :disabled="!canUpdate"
                        />
                    </label>
                    <label class="params-switch">
                        <span>開放線上</span>
                        <Switch v-model:checked="form.online_supported" :disabled="!canUpdate" />
                    </label>
                    <label class="params-switch">
                        <span>開放實體</span>
                        <Switch v-model:checked="form.offline_supported" :disabled="!canUpdate" />
                    </label>
                </div>
            </Card>
        </div>

        <Card :bordered="false" class="admin-panel" :loading="loading">
            <h2>每天可約的時段</h2>
            <div class="params-chips">
                <label v-for="start in slotOptions" :key="start" class="params-chip">
                    <input
                        type="checkbox"
                        :checked="form.daily_slots.includes(start)"
                        :disabled="!canUpdate"
                        @change="toggleSlot(start, $event.target.checked)"
                    />
                    {{ start }}
                </label>
            </div>
            <div v-if="canUpdate" class="params-add">
                <input v-model="extraStart" placeholder="20:30" maxlength="5" />
                <Button @click="addExtraSlot">加入時段</Button>
            </div>
        </Card>

        <Card :bordered="false" class="admin-panel" :loading="loading">
            <h2>每週不可約</h2>
            <div class="params-table-wrap">
                <table>
                    <thead>
                        <tr>
                            <th></th>
                            <th v-for="period in BOOKING_PERIODS" :key="period.value">
                                {{ period.label }}
                                <small>{{ period.hint }}</small>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="day in BOOKING_WEEKDAYS" :key="day.value">
                            <th>{{ day.label }}</th>
                            <td v-for="period in BOOKING_PERIODS" :key="period.value">
                                <input
                                    type="checkbox"
                                    :checked="Boolean(form.blackout[`${day.value}:${period.value}`])"
                                    :disabled="!canUpdate"
                                    @change="toggleBlackout(day.value, period.value, $event.target.checked)"
                                />
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </Card>
    </div>
</template>

<style scoped>
.params-page {
    display: flex;
    flex-direction: column;
    gap: 16px;
}
.params-head {
    display: flex;
    justify-content: space-between;
    gap: 16px;
    align-items: flex-start;
}
.params-head p,
.params-page p {
    margin: 0 0 12px;
    max-width: 40rem;
    color: #6b6458;
    font-size: 13px;
    line-height: 1.6;
}
.params-head label,
.params-fields label,
.params-create label {
    display: flex;
    flex-direction: column;
    gap: 8px;
    font-size: 13px;
    color: #3f3a33;
}
.params-page h2 {
    margin: 0 0 4px;
    font-size: 16px;
}
.params-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 16px;
}
.params-fields,
.params-create,
.params-chips,
.params-add {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-end;
}
.params-create {
    align-items: center;
}
.params-create :deep(.ant-input) {
    width: 180px;
}
.params-switch {
    min-width: 96px;
}
.params-chip {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border: 1px solid #ebe4d6;
    border-radius: 999px;
    background: #fff;
}
.params-grants {
    margin: 0 0 12px;
    padding-left: 18px;
    color: #3f3a33;
    font-size: 13px;
}
.params-add {
    margin-top: 12px;
    align-items: center;
}
.params-add input {
    width: 120px;
    height: 32px;
    padding: 0 10px;
    border: 1px solid #d9d9d9;
    border-radius: 6px;
}
.params-table-wrap {
    margin-top: 8px;
    overflow-x: auto;
}
table {
    border-collapse: collapse;
    min-width: 520px;
}
th,
td {
    padding: 10px 16px;
    text-align: center;
    border-bottom: 1px solid #f0ebe3;
}
th small {
    display: block;
    font-weight: 400;
    color: #8a8378;
}
tbody th {
    text-align: left;
}
@media (max-width: 960px) {
    .params-grid,
    .params-head {
        grid-template-columns: 1fr;
        display: flex;
        flex-direction: column;
    }
}
</style>
